import { useLocation, useParams } from "@tanstack/react-router";
import { type ComponentType } from "react";
import type { ModuleManifest } from "@praesto/contracts";
import { useCore } from "../lib/core";
import { SideNav, type SideNavItem } from "./SideNav";

/**
 * Sub-navigation for the active module. Reads `subRoutes[]` from the manifest
 * (RBAC-filtered) and renders them through the shared SideNav primitive.
 * Active state is computed here (we are under the router) and passed to SideNav,
 * which is router-agnostic.
 */
export function SubNavSidebar({ activeModule }: { activeModule: ModuleManifest | null }) {
  const core = useCore();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const location = useLocation();

  if (!activeModule) return null;

  const items: SideNavItem[] = (activeModule.subRoutes ?? [])
    .filter((s) => !s.requires || core.rbac.can(s.requires.action, s.requires.resource))
    .map((s) => {
      const to = `/${tenantSlug}/${activeModule.id}/${s.path}`;
      return {
        key: s.path,
        label: core.i18n.t(s.labelKey),
        icon: s.icon as ComponentType<{ size?: number; className?: string }>,
        to,
        active: location.pathname.startsWith(to),
      };
    });

  // A second sidebar with a single (or zero) destination is just visual noise —
  // there's nothing to navigate between. Hide it so a module with one sub-route
  // (the Home module historically) doesn't render a lonely one-item rail. The
  // module icon in the primary ModuleSidebar already routes into the module.
  if (items.length <= 1) return null;

  return <SideNav title={core.i18n.t(`${activeModule.id}.title`)} items={items} />;
}
